import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useChatNotesSync } from "./useLeadFormConfig";
import {
  invalidateFollowUpDashboardQueries,
  isFollowUpPatchType,
} from "@/features/Dashboard/api/invalidateFollowUpDashboardQueries";
import axiosInstance from "@/lib/axios";
import { toast } from "sonner";
import { assignChildEndpoint, childEndpoints } from "@/config/endpoints";
import {
  invalidateLeadBoard,
  invalidateLeadDetail,
  invalidateLeadTimeline,
  invalidateLeadAssignment,
} from "@/lib/queryClient";
import { mapLeadsInBoardCache, toCardNote } from "./useLeadMutations";
import type { QueryClient } from "@tanstack/react-query";

/**
 * Optimistic deal-note patch: a note added from the deal panel renders on the
 * PARENT lead card (pickLatestNote peeks each child's latest note). Find the
 * lead whose `child[]` contains `childId` and prepend the note to that child's
 * raw `notes` — so the card preview updates instantly with no board refetch.
 * Authoritative content (the user's text); row stays in place; no-op (harmless)
 * if the lead isn't on screen.
 */
function prependNoteToChildInBoardCache(
  queryClient: QueryClient,
  childId: string | number,
  note: any,
): boolean {
  const cid = Number(childId);
  if (!Number.isFinite(cid)) return false;
  // Same shape trap as the lead path — the drawer's note carries its text in
  // `note`, the card reads `content`. See `toCardNote`.
  const cardNote = toCardNote(note);
  if (!cardNote) return false;
  mapLeadsInBoardCache(queryClient, (lead) => {
    if (!lead || !Array.isArray(lead.child)) return lead;
    if (!lead.child.some((c: any) => Number(c?.id) === cid)) return lead;
    return {
      ...lead,
      child: lead.child.map((c: any) =>
        Number(c?.id) === cid
          ? { ...c, notes: [cardNote, ...(Array.isArray(c.notes) ? c.notes : [])] }
          : c,
      ),
    };
  });
  return true;
}

/**
 * INSTANT deal removal across every cache that renders a deal, so deleting a
 * deal disappears immediately — no waiting on the heavy `["get-leads"]` refetch
 * and no Chrome reload. Critically, it also strips the deal from the lead-detail
 * cache's `child[]`, because `ChildProvider.mergeLeadChildrenWithApi` UNIONS the
 * deals query with `lead.child`; if the deleted deal lingered there it would be
 * re-added even after `["childs"]` refetched without it. We still invalidate
 * afterwards for authoritative counts/rollups — the UI has just already updated.
 */
export function removeChildFromCaches(
  queryClient: QueryClient,
  childId: string | number,
) {
  const cid = Number(childId);
  if (!Number.isFinite(cid)) return;

  const dropFromLead = (lead: any) => {
    if (!lead || !Array.isArray(lead.child)) return lead;
    if (!lead.child.some((c: any) => Number(c?.id) === cid)) return lead;
    const child = lead.child.filter((c: any) => Number(c?.id) !== cid);
    const next: any = { ...lead, child };
    if (lead._count && typeof lead._count.child === "number") {
      next._count = { ...lead._count, child: Math.max(0, lead._count.child - 1) };
    }
    return next;
  };

  // Board cards (`["leads"]` — grid / infinite / status / stage board).
  mapLeadsInBoardCache(queryClient, dropFromLead);

  // Open lead-detail drawer (`["get-leads", leadId]` — the live source the
  // ChildProvider unions with the deals query).
  queryClient.setQueriesData<any>(
    { predicate: (q) => Array.isArray(q.queryKey) && q.queryKey[0] === "get-leads" },
    (lead: any) => dropFromLead(lead),
  );

  // Deals list query (`["childs", filters]` → `{ children: [...] }`).
  queryClient.setQueriesData<any>(
    { predicate: (q) => Array.isArray(q.queryKey) && q.queryKey[0] === "childs" },
    (data: any) => {
      if (!data || !Array.isArray(data.children)) return data;
      const children = data.children.filter((c: any) => Number(c?.id) !== cid);
      return children.length === data.children.length ? data : { ...data, children };
    },
  );
}

export function useChildMutations() {
  const queryClient = useQueryClient();
  // "Chat & notes sync": a note (or a follow-up / deadline note) saved here is
  // posted into the chat by the server. Mark cached threads stale — free, no
  // request — so a chat opened next shows it; open chats get the live push.
  const chatNotesSync = useChatNotesSync();
  const markChatStaleForNoteSync = () => {
    if (!chatNotesSync) return;
    queryClient.invalidateQueries({
      queryKey: ["internal-chat"],
      refetchType: "none",
    });
  };

  // 1. CREATE LEAD
  const createChild = useMutation({
    mutationFn: async (data: any) => {
      // Post to your backend (e.g., /api/leads)
      return axiosInstance.post(childEndpoints, data);
    },
    onSuccess: () => {
      toast.success("Child created successfully");
      // A new deal shows in the lead's Deals section + bumps the child
      // count on the board / header stats, and writes an activity row.
      // Actor-side only: other users' screens no longer refetch on our echo.
      invalidateLeadDetail();
      invalidateLeadBoard();
      invalidateLeadTimeline();
    },
    onError: (error: any) => {
      console.error(error);
      const msg = error.response?.data?.message || "Failed to create lead";
      toast.error(msg);
    },
  });

  // 2. UPDATE LEAD
  const updateChild = useMutation({
    mutationFn: async ({ id, data }: { id: any; data: any }) => {
      return axiosInstance.patch(`${childEndpoints}/${id}`, data);
    },
    onSuccess: () => {
      // A child field edit can change what the PARENT lead card renders —
      // custom-field/module/role rollups (aggregated across lead+children),
      // the payment chip, and the latest-note preview — plus the deal hover
      // tooltip. Refresh detail (drawer + hover, via invalidateLeadDetail)
      // AND the board cards. The edit also writes an activity row
      // (dataUpdate) — the timeline used to catch up via our socket echo.
      invalidateLeadDetail();
      invalidateLeadBoard();
      invalidateLeadTimeline();
    },
    onError: (error: any) => {
      const msg = error.response?.data?.message || "Failed to update lead";
      toast.error(msg);
    },
  });

  // Delete a deal. Server enforces the role wizard's per-stage
  // "Choices → Delete choices" toggle on the deal's stage (admins bypass).
  const deleteChild = useMutation({
    mutationFn: async (id: number | string) => {
      return axiosInstance.delete(`${childEndpoints}/${id}`);
    },
    onSuccess: (_data, id) => {
      toast.success("Deal deleted");
      // OPTIMISTIC: strip the deal from every cache that renders it so it
      // disappears instantly (no waiting on the heavy board refetch).
      removeChildFromCaches(queryClient, id);
      // Deal removal changes the parent lead card (deal count / payment
      // chip / latest note), the Deals section, and the activity timeline.
      // Invalidate afterwards for authoritative counts/rollups.
      invalidateLeadDetail();
      invalidateLeadBoard();
      invalidateLeadTimeline();
    },
    onError: (error: any) => {
      const msg = error.response?.data?.message || "Failed to delete deal";
      toast.error(msg);
    },
  });

  const updateStage = useMutation({
    mutationFn: async ({ id, data }: any) => {
      return await axiosInstance.patch(
        `${childEndpoints}/stage-update/${id}`,
        data
      );
    },
    onSuccess: () => {
      toast.success("Stage Updated");
      // A deal stage move shows on the deal board (under the `leads`
      // prefix) + the Deals section + writes an activity row.
      invalidateLeadBoard();
      invalidateLeadTimeline();
      // The drawer reads a deal from THREE caches and each must move:
      //   • `["get-child", id]`  — the active deal (Stage/status card);
      //   • `["get-leads", id]`  — the lead payload's own `child[]`;
      //   • `["childs", {leadId}]` — the deals list that feeds the Deals
      //     chips / stage strip / deal picker, and that WINS the
      //     `mergeLeadChildrenWithApi` union.
      // invalidateLeadDetail() busts all three (+ hover + tracks). Calling
      // refetchCurrentChild/Lead/Childs on top of it fired each GET twice
      // (the second invalidation cancels + restarts the in-flight fetch).
      invalidateLeadDetail();
    },
    onError: (error: any) => {
      const msg = error.response?.data?.message || "Failed to Update Stage";
      toast.error(msg);
    },
  });

  const patchChildSilently = useMutation({
    mutationFn: async ({
      id,
      data,
      type = "note",
    }: {
      id: string | number;
      data: any;
      type?: string;
    }) => {
      // We use PATCH to only update changed fields
      return await axiosInstance.patch(`${childEndpoints}/${id}`, {
        type,
        ...data,
      });
    },
    onSuccess: (_data, variables) => {
      invalidateLeadTimeline();
      invalidateLeadDetail();
      markChatStaleForNoteSync();

      const note = variables?.data?.note;
      const isNote = !isFollowUpPatchType(variables?.type) && !!note;
      if (isNote) {
        // OPTIMISTIC: a deal note only changes the parent card's latest-note
        // preview — patch it into the board cache instead of refetching the
        // heavy board query. (invalidateLeadDetail above already refreshes the
        // open drawer's deal panel.)
        if (!prependNoteToChildInBoardCache(queryClient, variables.id, note)) {
          invalidateLeadBoard();
        }
      } else {
        invalidateLeadBoard();
        if (isFollowUpPatchType(variables?.type)) {
          invalidateFollowUpDashboardQueries(queryClient);
        }
      }
    },
    onError: (error: any) => {
      const msg = error.response?.data?.message || "Auto-save failed";
      toast.error(msg);
    },
  });

  const assignChild = useMutation({
    mutationFn: async (data: {
      childIds: (string | number)[];
      userId: string | number;
      level?: "child";
    }) => {
      return axiosInstance.post(assignChildEndpoint, {
        children: data.childIds,
        userId: data.userId,
        level: data.level ?? "child",
      });
    },
    onSuccess: () => {
      toast.success("User assigned to application successfully");
      // Assigning at deal/child level still affects the lead-side caches
      // (drawer, board user stack, +N hover list) + assign eligibility.
      // Timeline: the assignment writes an activity row + may reset the
      // follow-up (our socket echo no longer refetches it).
      invalidateLeadAssignment();
      invalidateLeadTimeline();
    },
    onError: (error: any) => {
      console.error(error);
      const msg = error.response?.data?.message || "Failed to assign user";
      toast.error(msg);
    },
  });

  return {
    createChild,
    updateChild,
    deleteChild,
    updateStage,
    patchChildSilently,
    assignChild,
  };
}
